import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { invitationsAPI } from '../services/api';
import { getActivityLabel } from '../utils/carpoolUi';
import { PageLoading } from '../components/appPage';
import './Auth.css';

const backendFieldMessage = (field, message, t) => {
  if (message && message !== 'Invalid value') {
    return message;
  }

  const key =
    field === 'phone'
      ? 'auth.validation.phoneInvalid'
      : field === 'email'
        ? 'auth.validation.emailInvalid'
        : field === 'password'
          ? 'auth.passwordMinLength'
          : field === 'firstName'
            ? 'auth.validation.firstNameRequired'
            : field === 'lastName'
              ? 'auth.validation.lastNameRequired'
              : 'auth.validation.invalidValue';
  return t(key);
};

const describeValidationToast = (errors, t) => {
  const entries = Object.entries(errors || {}).filter(([, msg]) => Boolean(msg));
  if (entries.length === 0) {
    return t('auth.toast.validationFormInvalid');
  }
  if (entries.length === 1) {
    return t('auth.toast.validationFixOne', { detail: entries[0][1] });
  }
  return t('auth.toast.validationMultiple', {
    count: entries.length,
    detail: entries[0][1]
  });
};

const Register = () => {
  const { t } = useTranslation();
  const { register, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('invitation');

  const registerSchema = useMemo(
    () =>
      z.object({
        firstName: z.string().trim().min(1, t('auth.validation.firstNameRequired')),
        lastName: z.string().trim().min(1, t('auth.validation.lastNameRequired')),
        email: z.string().trim().email(t('auth.validation.emailInvalid')),
        password: z.string().min(6, t('auth.passwordMinLength')),
        phone: z
          .string()
          .trim()
          .optional()
          .or(z.literal(''))
          .refine(
            (value) => !value || /^\+?[0-9\s()-]{6,20}$/.test(value),
            t('auth.validation.phoneInvalid')
          ),
        invitationToken: z.string().optional()
      }),
    [t]
  );

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    invitationToken: invitationToken || ''
  });
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [invitationInfo, setInvitationInfo] = useState(null);
  const [validatingInvitation, setValidatingInvitation] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const validateInvitation = async () => {
      if (invitationToken) {
        setValidatingInvitation(true);
        try {
          const response = await invitationsAPI.validateInvitation(invitationToken);
          if (response.data.valid) {
            setInvitationInfo(response.data.invitation);
            if (response.data.invitation.receiverEmail) {
              setFormData((prev) => ({
                ...prev,
                email: response.data.invitation.receiverEmail.trim(),
                invitationToken: invitationToken
              }));
            } else {
              setFormData((prev) => ({
                ...prev,
                invitationToken: invitationToken
              }));
            }
          } else {
            toast.error(response.data.error?.message || t('errors.generic'));
          }
        } catch (err) {
          toast.error(err.response?.data?.error?.message || t('errors.generic'));
        } finally {
          setValidatingInvitation(false);
        }
      }
    };

    validateInvitation();
  }, [invitationToken, t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    setFieldErrors((prev) => {
      if (!prev[name]) {
        return prev;
      }
      return { ...prev, [name]: '' };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setFieldErrors({});

    const payload = {
      ...formData,
      phone: formData.phone.trim() || undefined,
      invitationToken: invitationToken || undefined
    };

    const validation = registerSchema.safeParse(payload);
    if (!validation.success) {
      const nextFieldErrors = {};
      validation.error.issues.forEach((issue) => {
        const path = issue.path?.[0];
        if (path && !nextFieldErrors[path]) {
          nextFieldErrors[path] = issue.message;
        }
      });
      setFieldErrors(nextFieldErrors);
      toast.error(describeValidationToast(nextFieldErrors, t));
      setLoading(false);
      return;
    }

    const result = await register(payload);

    if (result.success) {
      if (result.joinedCarpool) {
        toast.success(
          t('auth.toast.registerSuccessJoinedCarpool', {
            carpoolName: result.joinedCarpool.name
          })
        );
      } else {
        toast.success(t('auth.toast.registerSuccessRedirect'));
      }
      if (result.joinedCarpool) {
        navigate('/dashboard', {
          state: {
            message: t('invitations.joinedCarpoolSuccess', {
              carpoolName: result.joinedCarpool.name
            })
          }
        });
      } else {
        navigate('/dashboard');
      }
    } else if (result.fieldErrors && Object.keys(result.fieldErrors).length > 0) {
      const normalizedFieldErrors = {};
      Object.entries(result.fieldErrors).forEach(([field, message]) => {
        normalizedFieldErrors[field] = backendFieldMessage(field, message, t);
      });
      setFieldErrors(normalizedFieldErrors);
      toast.error(describeValidationToast(normalizedFieldErrors, t));
    } else {
      toast.error(result.error || t('auth.toast.registerFailed'));
    }

    setLoading(false);
  };

  if (validatingInvitation) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <PageLoading message={t('common.loading')} minHeight={false} />
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1 className="auth-title">{t('auth.register')}</h1>
        <p className="auth-subtitle">{t('common.appName')}</p>

        {invitationInfo && (
          <div className="invitation-banner">
            <h3 className="invitation-banner__title">
              {t('invitations.registerWithInvitation')}
            </h3>
            <p className="invitation-banner__text">
              {t('invitations.registerInviteLine', {
                senderName: invitationInfo.senderName,
                carpoolName: invitationInfo.carpoolName
              })}
            </p>
            <p className="invitation-banner__meta">
              {t('carpools.activity')}: {getActivityLabel(invitationInfo.carpoolActivity, t)}
            </p>
            <p className="invitation-banner__hint">
              {t('invitations.willJoinCarpool')}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">{t('auth.firstName')}</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
                placeholder={t('auth.firstName')}
                aria-invalid={Boolean(fieldErrors.firstName)}
              />
              {fieldErrors.firstName && (
                <small className="field-error">{fieldErrors.firstName}</small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="lastName">{t('auth.lastName')}</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                required
                placeholder={t('auth.lastName')}
                aria-invalid={Boolean(fieldErrors.lastName)}
              />
              {fieldErrors.lastName && (
                <small className="field-error">{fieldErrors.lastName}</small>
              )}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">{t('auth.email')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder={t('auth.email')}
              disabled={invitationInfo && invitationInfo.receiverEmail ? true : false}
              aria-invalid={Boolean(fieldErrors.email)}
            />
            {fieldErrors.email && <small className="field-error">{fieldErrors.email}</small>}
            {invitationInfo && invitationInfo.receiverEmail && (
              <small className="auth-field-note">
                {t('invitations.invitationEmailPre-filled')}
              </small>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="phone">{t('auth.phone')}</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder={t('auth.phone')}
              aria-invalid={Boolean(fieldErrors.phone)}
            />
            {fieldErrors.phone && <small className="field-error">{fieldErrors.phone}</small>}
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('auth.password')}</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength="6"
              placeholder={t('auth.password')}
              aria-invalid={Boolean(fieldErrors.password)}
            />
            {fieldErrors.password && <small className="field-error">{fieldErrors.password}</small>}
          </div>

          <button type="submit" disabled={loading} className="auth-button">
            {loading ? t('common.loading') : t('auth.register')}
          </button>
        </form>

        <p className="auth-footer">
          {t('auth.alreadyHaveAccount')}{' '}
          <Link to="/login">{t('auth.login')}</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
